import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useGeminiApi } from "@/hooks/useGeminiApi";
import { 
  FileText, 
  AlertCircle, 
  FileSearch, 
  ClipboardList, 
  CheckSquare, 
  PanelRight, 
  Info,
  CalendarCheck 
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { Separator } from "@/components/ui/separator";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export const TenderInformationTab = ({ tender }: { tender: any }) => {
  const [summary, setSummary] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { generateContent } = useGeminiApi();
  const { i18n } = useTranslation();
  const { currentLanguage } = useLanguage();

  const generateTenderSummary = async () => {
    if (!tender) return;
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const tenderData = JSON.stringify(tender);
      
      let systemInstruction = "";
      if (currentLanguage === "da") {
        systemInstruction = 
          "Du er en professionel udbudsanalytiker, der hjælper virksomheder med at forberede konkurrencedygtige tilbud. " +
          "Analysér udbudsdataene og opret en struktureret oversigt skræddersyet til potentielle tilbudsgivere. " +
          "Formatér dit svar i ren markdown med overskrifter for: Projektoverblik, Nøgledetaljer, " +
          "Krav, Adgangskriterier, Indleveringsproces, Evalueringsmetode og Strategiske Anbefalinger. " +
          "Vær omfattende men præcis. Hvis information mangler, angiv tydeligt 'Information ikke oplyst'. " +
          "Brug ikke triple backtick-blokke i dit svar, da de ikke gengives korrekt. " +
          "Start direkte med analysen. Undlad enhver form for indledende tekst som 'Jeg har analyseret' eller 'Okay'. " +
          "Afslut ikke med 'Held og lykke' eller lignende udtryk. Fokuser udelukkende på selve analysen. " +
          "Sørg for klar formatering med god afstand mellem afsnit og punkter. " +
          "Hold hvert punkt i listeform kort og præcist. " +
          "Dit svar skal være på dansk.";
      } else {
        systemInstruction = 
          "You are a professional tender analyst helping businesses prepare competitive bids. " +
          "Analyze the tender data and create a structured summary tailored for potential bidders. " +
          "Format your response in clean markdown with headings for: Project Overview, Key Details, " +
          "Requirements, Eligibility Criteria, Submission Process, Evaluation Method, and Strategic Recommendations. " +
          "Be comprehensive but concise. If information is missing, clearly state 'Information not provided'. " +
          "Do not use triple backtick blocks in your response as they will not render properly. " +
          "Begin directly with the analysis. Do not include any introductory text such as 'I have analyzed' or 'Okay'. " +
          "Do not end with 'Good luck' or similar phrases. Focus solely on the analysis itself. " +
          "Ensure clear formatting with proper spacing between sections and bullet points. " +
          "Keep each bullet point item short and concise.";
      }
      
      const prompt = currentLanguage === "da" 
        ? `Analysér venligst disse udbudsdata og opret en sammenfatning: ${tenderData}`
        : `Please analyze this tender data and create a summary: ${tenderData}`;
      
      const response = await generateContent(prompt, {
        model: "gemini-2.0-flash",
        temperature: 0.2,
        systemInstruction
      });
      
      if (response && response.generatedText) {
        const cleanedText = response.generatedText
          .replace(/```markdown\s*/g, '')
          .replace(/```\s*$/gm, '')
          .replace(/^# /gm, '');
        
        setSummary(cleanedText);
      } else {
        setError(currentLanguage === "da" 
          ? "Kunne ikke generere udbudssammenfatning. Prøv venligst igen." 
          : "Failed to generate tender summary. Please try again.");
      }
    } catch (err) {
      console.error("Error generating tender summary:", err);
      setError(currentLanguage === "da" 
        ? "Der opstod en fejl under generering af sammenfatningen. Prøv igen." 
        : "An error occurred while generating the summary. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (tender && !summary && !isGenerating) {
      generateTenderSummary();
    }
  }, [tender, currentLanguage]);

  const renderFormattedSummary = (markdownContent: string) => {
    const sections = markdownContent.split(/^## /m).filter(Boolean);
    
    if (sections.length === 0) {
      return <div className="text-sm">{markdownContent}</div>;
    }
    
    return (
      <div className="space-y-4">
        {sections.map((section, index) => {
          const lines = section.split('\n');
          const title = lines[0].trim();
          const content = lines.slice(1).join('\n').trim();
          
          if (!content.trim()) {
            return (
              <div key={`static-section-${index}`} className="py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-primary mr-2" />
                  <span className="font-medium text-base">{title}</span>
                </div>
              </div>
            );
          }
          
          let SectionIcon = FileText;
          if (title.includes("Executive Summary") || title.includes("Sammenfatning") || title.includes("Projektoverblik") || title.includes("Project Overview")) 
            SectionIcon = FileSearch;
          else if (title.includes("Key Details") || title.includes("Nøgledetaljer")) 
            SectionIcon = ClipboardList;
          else if (title.includes("Requirements") || title.includes("Krav")) 
            SectionIcon = CheckSquare;
          else if (title.includes("Eligibility") || title.includes("Adgangskriterier") || title.includes("Kvalifikationskrav")) 
            SectionIcon = Info;
          else if (title.includes("Submission") || title.includes("Indlevering")) 
            SectionIcon = PanelRight;
          else if (title.includes("Evaluation") || title.includes("Evaluering")) 
            SectionIcon = CalendarCheck;
          else if (title.includes("Recommendations") || title.includes("Anbefalinger")) 
            SectionIcon = Info;
          
          const formattedContent = content
            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            .replace(/\* (.+)$/gm, '<div class="flex items-start my-2"><span class="mr-2 mt-1 text-primary">•</span><span class="flex-1">$1</span></div>')
            .replace(/\d+\. (.+)$/gm, '<div class="flex items-start my-2"><span class="mr-2 font-bold">$&nbsp;</span><span class="flex-1">$1</span></div>')
            .replace(/\n\n/g, '<div class="my-4"></div>');
          
          return (
            <div key={`section-${index}`} className="border-b border-gray-200 last:border-0">
              <div className="py-4 bg-gray-50 rounded-t-lg px-4">
                <div className="flex items-center">
                  <SectionIcon className="h-5 w-5 text-primary mr-2" />
                  <span className="font-medium text-base">{title}</span>
                </div>
              </div>
              <div className="px-4 py-4 text-sm bg-white">
                <div 
                  className="prose max-w-none text-gray-700 space-y-2"
                  dangerouslySetInnerHTML={{ __html: formattedContent }} 
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSummaryContent = () => {
    if (isGenerating) {
      return (
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center py-8">
            <LoadingSpinner size={32} className="text-primary mb-4"/>
            <p className="text-muted-foreground">
              {currentLanguage === "da" 
                ? "Genererer udbudsanalyse..." 
                : "Generating tender analysis..."}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {currentLanguage === "da"
                ? "Dette kan tage et øjeblik..."
                : "This may take a moment..."}
            </p>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }
    
    if (!summary) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <FileText className="h-12 w-12 text-gray-400 mb-3" />
          <p className="text-muted-foreground">
            {currentLanguage === "da" 
              ? "Ingen udbudsinformation tilgængelig."
              : "No tender information available."
            }
          </p>
        </div>
      );
    }
    
    return renderFormattedSummary(summary);
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <CardHeader className="pb-2 flex flex-row justify-between items-center bg-gray-50 rounded-t-lg">
          <div className="flex items-center">
            <FileSearch className="h-5 w-5 text-primary mr-2" />
            <CardTitle className="text-xl">
              {currentLanguage === "da" ? "Udbudsanalyse" : "Tender Analysis"}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center text-sm text-muted-foreground mb-4 bg-blue-50 p-3 rounded-md">
            <AlertCircle className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
            <div>
              {currentLanguage === "da" 
                ? "Denne sammenfatning giver et struktureret overblik over offentliggjorte udbudsinformationer."
                : "This summary provides a structured overview of published tender information."}
            </div>
          </div>
          <Separator className="mb-4" />
          <div className="tender-summary">
            {renderSummaryContent()}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
